import React, { useState } from "react";

import { Col, Row } from "react-bootstrap";

import { useLocation } from "react-router-dom";

import {
  Participant,
  RssDocument,
  useGetPreSignedUploadUrlMutation,
  useNotifyUploadCompletionMutation,
} from "@api/graphql";
import { FormStep, MultiStepForm } from "@components/form/MultiStepForm";
import { useIsAuthUserAllowed } from "@components/permission_control/ProtectedComponent";
import { ResaleSubmitted } from "@components/resale_submission/ResaleSubmitted";
import { SelectParticipantCsv } from "@components/resale_submission/csv/SelectParticipantCsv";
import { CsvInput, UploadCsv } from "@components/resale_submission/csv/UploadCsv";
import { UploadReview } from "@components/resale_submission/csv/UploadReview";
import { Role } from "@roles";

import { ROUTES } from "../../routes";

import { DashboardHeader } from "./DashboardHeader";
import { ReuploadCsvSubmission } from "./ReuploadCsvSubmission";

import styles from "./ResaleCsvSubmission.module.scss";

export interface CsvSubmissionFormData {
  csvInput: CsvInput;
}

export const ResaleCsvSubmission = () => {
  const [getPreSignedUploadUrl] = useGetPreSignedUploadUrlMutation();
  const [notifyUploadCompletion] = useNotifyUploadCompletionMutation();
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [sourceFile, setSourceFile] = useState<RssDocument | undefined>(useLocation().state?.sourceFile);
  const recievedParticipant = useLocation().state?.participant;
  const reuploadMode = sourceFile !== undefined;
  const internal = useIsAuthUserAllowed([Role.Data, Role.VisualArtistAdmin]);
  const [participant, setParticipant] = useState<Participant | undefined>(useLocation().state?.participant);
  const isDataTeam = useIsAuthUserAllowed([Role.Data]);

  if (recievedParticipant !== undefined && participant === undefined) {
    setParticipant(recievedParticipant);
  }

  const participant_step = {
    route: ROUTES.ResaleCsvSubmission.$.SelectParticipant.relativePath,
    displayName: "Participant",
    component: SelectParticipantCsv,
    mergeKey: "csvInput",
  } as FormStep<CsvSubmissionFormData, CsvInput>;

  const upload_csv_step = {
    route: ROUTES.ResaleCsvSubmission.$.UploadCsv.relativePath,
    displayName: "Upload",
    component: UploadCsv,
    transitionSubmit: () =>
      reuploadMode
        ? ROUTES.ResaleCsvSubmission.$.ReuploadReview.relativePath
        : ROUTES.ResaleCsvSubmission.$.UploadReview.relativePath,
    mergeKey: "csvInput",
    additionalComponentProps: { sourceFile, participant },
  } as FormStep<CsvSubmissionFormData, CsvInput, { sourceFile: RssDocument | undefined; participant: Participant }>;

  const reupload_review_step = {
    route: ROUTES.ResaleCsvSubmission.$.ReuploadReview.relativePath,
    displayName: "Reupload Review",
    component: ReuploadCsvSubmission,
    additionalComponentProps: { sourceFile },
  } as FormStep<CsvSubmissionFormData, unknown, { sourceFile: RssDocument | undefined }>;

  const upload_review_step = {
    route: ROUTES.ResaleCsvSubmission.$.UploadReview.relativePath,
    displayName: "Review",
    component: UploadReview,
    transitionCancel: () => ROUTES.ResaleCsvSubmission.$.UploadCsv.relativePath,
    additionalComponentProps: { isSubmitting, participant },
  } as FormStep<CsvSubmissionFormData, unknown, { isSubmitting: boolean; participant: Participant }>;

  const steps = [
    ...(internal && !reuploadMode ? [participant_step, upload_csv_step] : [upload_csv_step]),
    ...(reuploadMode ? [reupload_review_step] : [upload_review_step]),
  ];

  const onSubmitCallback = async (multiFormData: CsvSubmissionFormData): Promise<void> => {
    if (!multiFormData.csvInput?.file) {
      setErrorMessage("No file selected");
      return;
    }

    const file = multiFormData.csvInput.file;
    setIsSubmitting(true);

    try {
      // Step 1: Get pre-signed URL
      const { data } = await getPreSignedUploadUrl({
        variables: { fileName: file.name, fileType: file.type },
      });

      const uploadUrl = data?.getPreSignedUploadURL?.uploadUrl;
      const key = data?.getPreSignedUploadURL?.key;

      if (!uploadUrl || !key) {
        setErrorMessage("Failed to get pre-signed URL");
        setIsSubmitting(false);
        return;
      }

      // Step 2: Upload the file to S3
      const response = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type,
        },
      });

      if (!response.ok) {
        setErrorMessage("File upload failed");
        setIsSubmitting(false);
        return;
      }

      // Step 3: Notify backend of completion
      const notifyResponse = await notifyUploadCompletion({
        variables: {
          input: {
            key: key,
            curated: false,
            ...(participant && { participant_id: participant.participant_id }),
          },
        },
      });

      if (!notifyResponse.data?.notifyUploadCompletion?.success) {
        setErrorMessage("Failed to notify backend of upload completion");
        setIsSubmitting(false);
        return;
      }

      console.log("File uploaded and backend notified successfully");
      setIsSubmitted(true);
      setErrorMessage(null); // clear any previous error message
    } catch (error) {
      setErrorMessage("Error during file upload process: " + error);
      console.error("Error during file upload process", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <DashboardHeader></DashboardHeader>
      <div className={styles["content"]}>
        <Row className="justify-content-center">
          {!isSubmitted ? (
            <Col>
              {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
              <MultiStepForm<CsvSubmissionFormData, CsvInput>
                baseRoute={ROUTES.ResaleCsvSubmission.path}
                steps={steps}
                onSubmit={onSubmitCallback}
              />
            </Col>
          ) : (
            <Col className="col-md-6">
              <ResaleSubmitted
                buttonRoute={isDataTeam ? ROUTES.Dashboard.RecentReports.path : ROUTES.Dashboard.path}
                buttonText={isDataTeam ? "Recent reports" : "Back to dashboard"}
                messageText="The resale royalty team will check your uploaded file. Your submitted resale reports will be available
                  to view in your dashboard soon. We’ll notify you of resales eligible for royalty collection."
              />
            </Col>
          )}
        </Row>
      </div>
    </>
  );
};
